import { api } from "../../lib";
import { TemplateContent } from "../../types/reportTemplate";

export interface ReportTemplate {
  id: string;
  name: string;
  description: string | null;
  templateContent: TemplateContent | null;
  headerHtml: string | null;
  footerHtml: string | null;
  cssStyles: string | null;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateReportTemplateInput {
  name: string;
  description?: string;
  templateContent?: TemplateContent;
  headerHtml?: string;
  footerHtml?: string;
  cssStyles?: string;
  isDefault?: boolean;
  isActive?: boolean;
}

export interface UpdateReportTemplateInput
  extends Partial<CreateReportTemplateInput> {
  id: string;
}

const ACTIVE_TEMPLATES_RESPONSE_ERROR =
  "Active report templates response was not a valid array";

function normalizeActiveReportTemplates(payload: unknown): ReportTemplate[] {
  const decodedPayload = decodeJsonPayload(payload);
  const templateList = extractTemplateList(decodedPayload);
  if (!Array.isArray(templateList) || !templateList.every(isReportTemplate)) {
    throw new TypeError(ACTIVE_TEMPLATES_RESPONSE_ERROR);
  }
  return templateList;
}

function decodeJsonPayload(payload: unknown): unknown {
  if (typeof payload !== "string") return payload;
  try {
    return JSON.parse(payload);
  } catch {
    throw new TypeError(ACTIVE_TEMPLATES_RESPONSE_ERROR);
  }
}

function extractTemplateList(payload: unknown): unknown {
  if (typeof payload !== "object" || payload === null) return payload;
  if ("data" in payload) return (payload as { data: unknown }).data;
  return payload;
}

function isReportTemplate(value: unknown): value is ReportTemplate {
  if (!isRecord(value)) return false;
  return (
    typeof value.id === "string" &&
    typeof value.name === "string" &&
    isNullableString(value.description) &&
    (value.templateContent === null || isRecord(value.templateContent)) &&
    isNullableString(value.headerHtml) &&
    isNullableString(value.footerHtml) &&
    isNullableString(value.cssStyles) &&
    typeof value.isDefault === "boolean" &&
    typeof value.isActive === "boolean" &&
    typeof value.createdAt === "string" &&
    typeof value.updatedAt === "string"
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isNullableString(value: unknown): boolean {
  return typeof value === "string" || value === null;
}

export const reportTemplatesService = {
  getAll: async () => {
    return api.get<ReportTemplate[]>("/admin/report-templates");
  },

  getActive: async () => {
    const payload = await api.get<unknown>("/admin/report-templates/active");
    return normalizeActiveReportTemplates(payload);
  },

  getById: async (id: string) => {
    return api.get<ReportTemplate>(`/admin/report-templates/${id}`);
  },

  create: async (input: CreateReportTemplateInput) => {
    return api.post<ReportTemplate>("/admin/report-templates", input);
  },

  update: async (input: UpdateReportTemplateInput) => {
    const { id, ...updates } = input;
    return api.put<ReportTemplate>(`/admin/report-templates/${id}`, updates);
  },

  delete: async (id: string) => {
    await api.delete<void>(`/admin/report-templates/${id}`);
  },

  setDefault: async (id: string) => {
    return api.post<ReportTemplate>(
      `/admin/report-templates/${id}/set-default`
    );
  },
};
